import { type ParsedUrlQuery } from 'querystring';
import { getLuminance } from 'polished';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { nanoid } from 'nanoid';
import { type SingletonRouter, type NextRouter } from 'next/router';
import {
  ALLOWED_URL_PARAMETER_KEYS,
  ALLOWED_URL_PARAMETER_VALUES,
  KOALA_SESSION_STORAGE_KEYS,
} from '@/constants/global';
import { LOYALTY_ROUTES, ROUTES } from '@/constants/routes';
import { type IErrorResponse } from '@/types/errors';
import { captureMessage } from '@/utils/errorReporter';
import { isDinerFacingError } from '@/utils/dinerFacingError';
import { getErrorDataObject } from '@/utils/errorEnvelope';
import { resolveDinerMessage } from '@/utils/fpoErrors';
import { type IBasketState } from '@/types/basket';
import { SESSION_STORAGE_KEYS } from '@/constants/storageKeys';

// Generic method to group an array of objects by a specific key
// currently used to group locations by state and cart items by filter tag
/** @TODO refactor this utility to correctly use generics. */
export const genericGroupItemsByKey = (array: {}[], stringPathToKey: string, uniqueBy?: string) => {
  // `stringPathToKey` is the path to the key you want to sort by - accessible via the lodash 'get' method
  const filtersObject = {};
  const itemDict = {};
  // @ts-expect-error
  let filtersArray = [];

  if (!array) {
    // @ts-expect-error
    return { filtersArray, filtersObject };
  }

  array.forEach((arrayItem, index) => {
    const entry = get(arrayItem, stringPathToKey, false);

    // Don't push duplicate items if a uniqueBy property is defined
    if (uniqueBy) {
      // @ts-expect-error
      if (itemDict[arrayItem[uniqueBy]]) {
        return;
      }
      // @ts-expect-error
      itemDict[arrayItem[uniqueBy]] = true;
    }
    // @ts-expect-error
    filtersObject[entry] = filtersObject[entry] || [];
    // @ts-expect-error
    filtersObject[entry].push({ ...arrayItem, index });
  });

  // Derive an array of key strings
  filtersArray = Object.keys(filtersObject);
  filtersArray.sort();

  // Order the entries within each filtersObject key
  Object.keys(filtersObject).forEach((key) => {
    // @ts-expect-error
    filtersObject[key] = orderBy(
      // @ts-expect-error
      filtersObject[key],
      ['entry', 'label'],
      ['asc', 'asc'],
    );
  });

  return { filtersArray, filtersObject };
};

/**
 * Disable body scroll when scrollable / fixed overlays appear
 *
 * @param {Boolean} scroll
 */
export const toggleBodyScroll = (scroll?: boolean) => {
  if (scroll === undefined) {
    scroll = document.body.classList.contains('no-scroll');
  }
  if (!scroll) {
    document.body.classList.add('no-scroll');
  } else {
    document.body.classList.remove('no-scroll');
  }
};

/**
 * Calculate the scrollTo offset dependent on which header style is used.
 */
export const scrollToOffset = (elementTop = 0, additionalOffset = 0) => {
  if (typeof window === 'undefined') {
    return 0;
  }

  return elementTop + window.pageYOffset - additionalOffset;
};

/**
 * Calculate the x & y offsets of an document element
 */
export const getElementOffset = (el: HTMLElement | null) => {
  let _x = 0;
  let _y = 0;

  while (el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop)) {
    _x += el.offsetLeft - el.scrollLeft;
    _y += el.offsetTop - el.scrollTop;
    // @ts-expect-error assigning Element to HTMLElement
    el = el.offsetParent;
  }

  return { top: _y, left: _x };
};

/**
 * Assemble the raw technical text from a coo-api error envelope.
 *
 * TEAM-13882: this is `detail`, not `message`. It exists for classification (the substring tables in
 * `@/constants/basket` and `isPaymentError`) and for Sentry. It must never be shown to a diner —
 * that is what `resolveDinerMessage` is for.
 *
 * Note the `else if` below. `typeof null === 'object'`, so an envelope with
 * `error_description: null` and no `failures` used to append the bare machine slug (`access_denied`,
 * `bad_request`) to the diner's toast. TEAM-13451 patched that symptom locally in the sign-up saga;
 * keeping the slug in `detail` generalises the fix.
 */
export const deriveErrorDetail = (error: IErrorResponse | null): string => {
  if (!error) {
    return '';
  }

  let detail = '';

  if (typeof error.error_description === 'string') {
    detail += `${error.error_description} `;
  }

  const failures = getErrorDataObject(error)?.failures;
  if (failures) {
    detail += Object.values(failures).join(', ');
  } else if (typeof error.error_description === 'object' && typeof error.error === 'string') {
    detail += error.error;
  }

  return detail;
};

export interface PreparedErrorMessage {
  /**
   * Diner-safe copy, resolved by the TEAM-13882 render rule. Upstream text is HTML-escaped for the
   * global toast's `dangerouslySetInnerHTML`. Decode entities once at React text display sites;
   * never decode before an HTML sink. Authored catalog and static copy are unchanged.
   */
  message: string;
  /**
   * Raw technical text from the error envelope. **Never render this.** Classification and Sentry
   * only.
   */
  detail: string;
  error: IErrorResponse | null;
}

function isResponse(error: unknown): error is Response {
  return typeof (error as Response).text === 'function';
}

/**
 * The single funnel from a thrown API error to something a diner can read. TEAM-13882: `message` is
 * the only field safe to display, `detail` carries the raw upstream text for classification and
 * Sentry. Resolving here rather than at each display site means the *default* name is the safe one.
 *
 * @see resolveDinerMessage for the render rule applied to `message`
 */
export async function prepareErrorMessage(
  staticMessage: string | null,
  errorResponse: unknown = null,
): Promise<PreparedErrorMessage> {
  let detail = '';
  let error: IErrorResponse | null = null;

  /**
   * The one construction site for the return value, so no exit can bypass the render rule. Pass
   * `message` only for the branches that already know their own diner copy.
   */
  const resolve = (message?: string): PreparedErrorMessage => ({
    message: message ?? resolveDinerMessage({ error, staticMessage }),
    detail,
    error,
  });

  // A locally-thrown error that deliberately carries its own diner copy. Checked first: it is the
  // most specific case, and `detail` stays empty so nothing downstream can classify behaviour off
  // copy we authored ourselves (the coupling this ticket exists to remove). See `DinerFacingError`.
  if (isDinerFacingError(errorResponse)) {
    return resolve(errorResponse.dinerMessage);
  }
  if (errorResponse === null) {
    return resolve();
  }
  if (isResponse(errorResponse)) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      error = await errorResponse
        .text()
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        .then((text) => (text ? JSON.parse(text) : {}));
    } catch (e) {
      console.error(e, errorResponse.text());
    }
    detail = deriveErrorDetail(error);
  } else if (errorResponse && typeof errorResponse === 'object' && 'text' in errorResponse) {
    const { status, text, url } = errorResponse as {
      status?: number;
      text?: string;
      url?: string;
    };
    if (status === 429) {
      // Rate-limited at the edge: the body is plain text, not JSON, and retrying
      // extends the lockout — return the copy directly with no error details (TEAM-14778).
      return resolve('Too many attempts — please wait about an hour before trying again.');
    }
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      error = JSON.parse(text!);
      detail = deriveErrorDetail(error);
    } catch {
      // Non-JSON error body (e.g. a proxy or edge response): report the body to Sentry and keep it
      // out of both fields. It is not a coo-api envelope, so it classifies nothing, and the toast
      // renders with dangerouslySetInnerHTML — an HTML error page or reflected content would be
      // injected as markup.
      captureMessage('prepareErrorMessage: non-JSON error body', 'warning', {
        status,
        url,
        body: String(text).slice(0, 500),
      });
    }
  } else {
    // A local exception, an SDK sentinel, or a bare string. Its text is technical by default, so it
    // goes to `detail`. Anything here that should reach a diner must say so with a
    // `DinerFacingError` rather than relying on stringification.
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    detail = `${errorResponse}`;
  }

  return resolve();
}

/**
 * Given a pathname, derive a user-facing page title
 */
export const derivePageNameFromPath = (pathname?: string) => {
  let pageName = '';

  if (!pathname) {
    return pageName;
  }

  // Iterate through a pathname array
  pathname.split('/').forEach((pathParam: string) => {
    if (pathParam) {
      // Remove all dashes
      const sanitizedPathParam = pathParam.replace(/-/g, ' ');

      // Uppercase first letter & add pipe
      pageName += `${sanitizedPathParam.charAt(0).toUpperCase()}${sanitizedPathParam.slice(1)} | `;
    }
  });

  return decodeURI(pageName);
};

/**
 * Return a valid referrer for use on post sign-up or login
 */
export const deriveReferrer = (router: {
  query: { referrer?: string };
  pathname: string;
  asPath: string;
}) => {
  // Default to homepage redirect
  let referrer = ROUTES.HOMEPAGE;

  // /checkout or /store/[...id] are valid referrer pages
  if (
    [
      ROUTES.CHECKOUT,
      ROUTES.STORE,
      ROUTES.ANDROID,
      LOYALTY_ROUTES.REWARDS,
      LOYALTY_ROUTES.FAVORITES,
    ].includes(router?.pathname)
  ) {
    referrer = router?.pathname;

    // If it's a store page, we have to swap the pathname (/store/[...id]) for the asPath (/store/{storeId}/{storeName})
    if (router.pathname === ROUTES.STORE) {
      referrer = router?.asPath;
    }
  }

  // BUT! if we have an existing referrer, lets just pass that along
  return encodeURIComponent(router?.query?.referrer || referrer);
};

/**
 * Return a valid referrer for use on post sign-up or login
 */
export const parseReferrer = (router: SingletonRouter | NextRouter) => {
  // Either we'll use the existing referrer or the homepage
  let referrer = router?.query?.referrer || ROUTES.HOMEPAGE;

  // If we're on the checkout page, we do not want to redirect
  if (ROUTES.CHECKOUT === router.pathname) {
    referrer = '';
  }

  return referrer;
};

export const copyToClipboard = (str?: string | null) => {
  if (!str) {
    return;
  }

  // Works for all browsers except IE
  const copySuccess = navigator.clipboard.writeText(str).then(
    () =>
      /* clipboard successfully set */
      true,
    () =>
      /* clipboard write failed */
      false,
  );

  return copySuccess;
};

export const getCanonicalUrl = (baseUrl: string, router: NextRouter) => {
  // Remove hashes and query params from url
  let path = router.asPath.split(/[?#]/)[0];

  // Standardize store urls
  if (router.pathname === ROUTES.STORE) {
    /** @TODO guard to ensure that query.id is defined. */
    // @ts-expect-error
    path = `/store/${router.query.id[0]}`;
  }

  return `${baseUrl}${path}`;
};

/**
 * Derive a collection of persistable parameters from a given collection of URL parameters.
 *
 * @param parameters Record<string, string>
 * @param allowUnlisted boolean
 * @returns Record<string, string>
 */
export const derivePersistentURLParameters = (parameters: ParsedUrlQuery): ParsedUrlQuery => {
  const derivedParameters: ParsedUrlQuery = {};

  Object.keys(parameters).forEach((param) => {
    /** @TODO ensure that `parameters[param]` is defined before use. */
    // @ts-expect-error
    if (isPersistentParameterAllowed(param, parameters[param].toString())) {
      derivedParameters[param] = parameters[param];
    }
  });

  return derivedParameters;
};

/**
 * Determine if a parameter and its value are allowed to persist
 *
 * @param parameter string
 * @param value string
 * @return boolean;
 */
export const isPersistentParameterAllowed = (parameter: string, value: string): boolean => {
  if (!Object.values(ALLOWED_URL_PARAMETER_KEYS).includes(parameter)) {
    return false;
  }

  // Check if value is valid (empty [] indicate any value is acceptable and is also valid)
  if (
    ALLOWED_URL_PARAMETER_VALUES[parameter].includes(value) ||
    ALLOWED_URL_PARAMETER_VALUES[parameter].length === 0
  ) {
    return true;
  }

  return false;
};

/**
 * Get a persistent parameter value from session storage
 *
 * @param parameter string
 * @returns string
 */
export const getPersistentParameterValue = (parameter: string): string => {
  try {
    const sessionParameters = JSON.parse(
      sessionStorage.getItem(KOALA_SESSION_STORAGE_KEYS.PARAMETERS) || '{}',
    );

    return sessionParameters[parameter];
  } catch (err) {
    if (err instanceof Error) {
      // This is the same error that Safari private browsing will return
      if (err.name === 'QUOTA_EXCEEDED_ERR') {
        console.error('We have exceeded the allowed storage quota');
      }
    }

    console.error(err);

    return '';
  }
};

export function removeScriptFromDOM(scriptSource: string) {
  const script = document.querySelector(`script[src="${scriptSource}"]`);

  script?.parentNode?.removeChild(script);
}

export function isScriptInDOM(scriptSource: string) {
  return Boolean(document.querySelector(`script[src="${scriptSource}"]`));
}

export function addScriptToDOM(scriptSource: string, callBackOnLoad: () => void) {
  const script = document.createElement('script');

  script.src = scriptSource;
  //script is executed when the page has finished parsing.
  //https://developer.mozilla.org/en-US/docs/Web/API/Window/DOMContentLoaded_event
  script.defer = true;

  script.onload = callBackOnLoad;

  script.onerror = (event: Event | string) => {
    const src =
      typeof event === 'string'
        ? scriptSource
        : // NOTE: have to cast target to HTMLScriptElement as in ts definition target is a EventTarge type which does not have src.
          // But in current context it is a HTMLScriptElement
          (event.target as HTMLScriptElement | null)?.src;

    console.warn(`The script "${src}" didn't load correctly.`);
  };

  document.body.appendChild(script);
}

/**
 * How long a session basket stays fresh after the last read or write. Shared with the
 * "Save Cart for Later" copy (TEAM-15276, `utils/savedCart.ts`), which must not outlive it.
 */
export const SESSION_BASKET_TTL_MS = 2 * 60 * 60 * 1000;

export const sessionStorageBasket = {
  get: (): IBasketState | null => {
    const basketData = sessionStorage.getItem(SESSION_STORAGE_KEYS.KOALA_ORDER);
    const orderStartTime = sessionStorage.getItem(SESSION_STORAGE_KEYS.ORDER_START);
    if (!orderStartTime) return null;
    const startTime = new Date(orderStartTime).getTime();
    const currentTime = Date.now();
    const expiryTime = startTime + SESSION_BASKET_TTL_MS;
    const hasExpired = currentTime >= expiryTime;

    if (basketData && hasExpired) {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.ORDER_START, new Date().toISOString());
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.KOALA_ORDER);
      return null;
    }

    sessionStorage.setItem(SESSION_STORAGE_KEYS.ORDER_START, new Date().toISOString());
    if (!basketData) return null;
    return JSON.parse(basketData);
  },
  set: (basket: any): void => {
    const sessionId = sessionStorage.getItem(SESSION_STORAGE_KEYS.SESSION_ID);
    sessionStorage.setItem(SESSION_STORAGE_KEYS.ORDER_START, new Date().toISOString());

    if (!sessionId) {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.SESSION_ID, nanoid());
    }

    sessionStorage.setItem(SESSION_STORAGE_KEYS.KOALA_ORDER, JSON.stringify(basket));
  },
  remove: (): void => {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.ORDER_START, new Date().toISOString());
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.KOALA_ORDER);
  },
};

/**
 * The unsatisfied-options prompt the diner has already been shown — TEAM-15138.
 *
 * ## Why this exists at all
 *
 * `revalidateBasketItemsSaga` runs on **every** route change with a non-empty basket (`_app.tsx`'s
 * hydrate effect is keyed on `router.pathname`). Its two older verdicts survive that because they
 * are self-clearing *by mutation*: price drift rewrites the cost and a vanished product is removed,
 * so the next run finds nothing to say. The option-group verdict mutates nothing — the item stays
 * invalid until the diner reopens it — so without a memory its toast would reappear on the store
 * page, the cart, checkout and every back-navigation, and a dismissible nag is one the diner learns
 * to dismiss without reading.
 *
 * ## One key for the whole cart, not one per line
 *
 * The stored value is a digest of *every* unsatisfied line's option signature, in cart order — see
 * `unsatisfiedOptionsPromptKey`. Per-line keys were tried first and are wrong in the case that
 * matters most: two identical lines share a signature, and anything positional shifts the moment
 * one of them is fixed, so the survivor inherits a key already recorded and is silenced forever.
 * A whole-set digest changes whenever the set changes — a line fixed, a new one broken, the menu
 * moving again — which is exactly when the diner should hear from us, and does not change when
 * they simply ignore the toast.
 *
 * Its own sessionStorage key rather than a field on the basket: see `SESSION_STORAGE_KEYS`.
 */
export const sessionStorageOptionPrompts = {
  /** The digest of the last prompt shown, or `null` if none is outstanding. */
  get: (): string | null => {
    try {
      return sessionStorage.getItem(SESSION_STORAGE_KEYS.OPTION_PROMPTS_SHOWN);
    } catch {
      // Storage unavailable (private mode). Losing the memory only costs a repeat prompt.
      return null;
    }
  },
  /** `null` clears it, which is what a cart with nothing unsatisfied should leave behind. */
  set: (key: string | null): void => {
    try {
      if (key === null) {
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.OPTION_PROMPTS_SHOWN);
        return;
      }
      sessionStorage.setItem(SESSION_STORAGE_KEYS.OPTION_PROMPTS_SHOWN, key);
    } catch {
      // As above.
    }
  },
};

export function isNumber(value: unknown): value is number {
  return (
    (typeof value === 'number' && !isNaN(value)) ||
    (typeof value === 'string' && !isNaN(Number(value)))
  );
}

/** Try catch helper  */
export const tryCatch = async <T>(promise: Promise<T>): Promise<[T | null, Error | null]> => {
  try {
    const data = await promise;
    return [data, null];
  } catch (e) {
    const err = e instanceof Error ? e : new Error(String(e));
    return [null, err];
  }
};

export const isDark = (color: string) => getLuminance(color) < 0.5;

/**
 * Type-safe enum value checker
 * @param enumObj The enum object to check against
 * @param value The value to check
 * @returns Whether the value is a valid enum member
 */
export function isEnumValue<T extends Record<string, unknown>>(
  enumObj: T,
  value: unknown,
): value is T[keyof T] {
  return Object.values(enumObj).includes(value);
}

/**
 * Safe enum comparison that satisfies TypeScript's type checking
 * @param value1 First value to compare
 * @param value2 Second value to compare
 * @param enumObj The enum object both values should belong to
 * @returns Whether the values are equal and both valid enum members
 */
export function safeEnumCompare<T extends Record<string, unknown>>(
  value1: unknown,
  value2: unknown,
  enumObj: T,
): boolean {
  return isEnumValue(enumObj, value1) && isEnumValue(enumObj, value2) && value1 === value2;
}

export const createStateDiff = (
  current: Record<string, any>,
  previous: Record<string, any> | undefined,
): { changed: boolean; diff: Record<string, { from: any; to: any }> } => {
  if (!previous) {
    return { changed: false, diff: {} };
  }

  const diff: Record<string, { from: any; to: any }> = {};
  let changed = false;

  Object.keys(current).forEach((key) => {
    if (current[key] !== previous[key]) {
      changed = true;
      diff[key] = {
        from: previous[key],
        to: current[key],
      };
    }
  });

  return { changed, diff };
};
